import { useCallback, useMemo } from 'react';

const TOOLTIP_ID = 'exploration-tooltip';

/** All variants use the white tip-card shell. `status`/`volume` use the `sc vt` width (360px). */
export type TooltipVariant = 'group' | 'status' | 'volume';

function positionTooltip(tip: HTMLElement, x: number, y: number) {
  const r = tip.getBoundingClientRect();
  const spaceBelow = window.innerHeight - y - 12;
  const top = spaceBelow >= r.height + 8 ? y + 12 : Math.max(y - 12 - r.height, 8);
  tip.style.left = `${Math.min(x + 12, window.innerWidth - r.width - 8)}px`;
  tip.style.top = `${top}px`;
}

function onDocumentMouseMove(e: MouseEvent) {
  const tip = document.getElementById(TOOLTIP_ID);
  if (!tip?.classList.contains('show')) return;
  positionTooltip(tip, e.clientX, e.clientY);
}

if (typeof document !== 'undefined') {
  document.addEventListener('mousemove', onDocumentMouseMove);
}

export function useExplorationTooltipHandlers() {
  const showTooltip = useCallback(
    (html: string, x: number, y: number, variant: TooltipVariant = 'volume') => {
      const tip = document.getElementById(TOOLTIP_ID);
      if (!tip) return;
      tip.innerHTML = html;
      tip.classList.remove('tip-card', 'sc', 'vt');
      tip.classList.add('tip-card');
      if (variant === 'status' || variant === 'volume') {
        tip.classList.add('sc', 'vt');
      }
      tip.classList.add('show');
      tip.setAttribute('aria-hidden', 'false');
      positionTooltip(tip, x, y);
    },
    []
  );

  const hideTooltip = useCallback(() => {
    const tip = document.getElementById(TOOLTIP_ID);
    if (!tip) return;
    tip.classList.remove('show', 'tip-card', 'sc', 'vt');
    tip.setAttribute('aria-hidden', 'true');
  }, []);

  return useMemo(() => ({ showTooltip, hideTooltip }), [showTooltip, hideTooltip]);
}
